import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import Card from '../../Card/Card';
import SpirisIcon from '../../Fundamentals/Icon/SpirisIcon';
import RowActionsMenu from '../../Fundamentals/RowActionsMenu/RowActionsMenu';
import usePlanFormat from './usePlanFormat';
import { VossBindingPeriodEndActionText, VossPeriodEndAnchorText, VossPlanChangeBehaviourText } from '../../../Constants/VOSS/vossPlanEnums';
import type { BillingPeriodCycle, PackagePlan } from './Packages.types';
import './PackagePlanCard.css';

interface PackagePlanCardProps {
    plan: PackagePlan;
    canEdit: boolean;
    onEdit: () => void;
    onDelete: () => void;
}

const CycleColumn = ({ icon, title, rows }: { icon: string; title: string; rows: [string, ReactNode][] }) => (
    <div className="flex-1 min-w-0">
        <div className="d-flex align-items-center gap-6 font-weight-600 font-13 mb-8">
            <SpirisIcon name={icon} size="sm" />
            {title}
        </div>
        {rows.map(([label, value]) => (
            <div key={label} className="d-flex justify-content-between gap-12 font-13 py-2">
                <span className="text-secondary text-nowrap">{label}</span>
                <span className="package-plan-mono text-right">{value}</span>
            </div>
        ))}
    </div>
);

const PackagePlanCard = ({ plan, canEdit, onEdit, onDelete }: PackagePlanCardProps) => {
    const { t } = useTranslation();
    const { formatCycleLength, formatOffset } = usePlanFormat();

    const externalId = plan.externalId ?? '';
    const binding = plan.bindingPeriodCycle;

    const billingRows = (cycle: BillingPeriodCycle | null | undefined): [string, ReactNode][] => [
        [t('packages.period'), formatCycleLength(cycle?.length)],
        [t('packages.invoice'), formatOffset(cycle?.invoiceDateOffset)],
        [t('packages.renewal'), formatOffset(cycle?.renewalDateOffset)],
        ...(cycle?.notBillablePeriodRenewalDateOffset === null || cycle?.notBillablePeriodRenewalDateOffset === undefined
            ? []
            : ([[t('packages.notBillableRenewal'), formatOffset(cycle.notBillablePeriodRenewalDateOffset)]] as [string, ReactNode][])),
    ];

    return (
        <Card>
            <Card.Content>
                <div className="d-flex align-items-center gap-12">
                    <span className="package-plan-icon d-flex align-items-center justify-content-center flex-shrink-0">
                        <SpirisIcon name="credit-card" />
                    </span>
                    <div className="flex-1 min-w-0">
                        <div className="font-weight-600 font-16">{plan.name}</div>
                        <div className={`text-secondary${externalId.length > 0 ? ' package-plan-mono' : ' font-size-xs font-style-italic'}`}>
                            {externalId.length > 0 ? externalId : t('packages.noExternalId')}
                        </div>
                    </div>
                    <div className="d-flex flex-wrap gap-6 justify-content-end">
                        {plan.periodEndAnchor !== undefined && (
                            <span className="label">
                                {t('packages.periodEnd')}: {t(VossPeriodEndAnchorText[plan.periodEndAnchor])}
                            </span>
                        )}
                        {plan.changeBehaviour !== undefined && plan.changeBehaviour !== null && (
                            <span className="label">{t(VossPlanChangeBehaviourText[plan.changeBehaviour])}</span>
                        )}
                    </div>
                    {canEdit && (
                        <RowActionsMenu
                            label={t('packages.planActions') ?? ''}
                            actions={[
                                { id: 'edit', label: t('packages.editPlan') ?? '', icon: 'edit', onClick: onEdit },
                                { id: 'delete', label: t('packages.deletePlan') ?? '', icon: 'delete', destructive: true, onClick: onDelete },
                            ]}
                        />
                    )}
                </div>
                <div className="d-flex flex-wrap gap-24 border-top mt-16 pt-16">
                    <CycleColumn
                        icon="lock"
                        title={t('packages.bindingCycle') ?? ''}
                        rows={[
                            [t('packages.length'), formatCycleLength(binding?.length)],
                            [t('packages.atPeriodEnd'), binding?.periodEndAction === undefined ? '—' : t(VossBindingPeriodEndActionText[binding.periodEndAction])],
                        ]}
                    />
                    <CycleColumn icon="credit-card" title={t('packages.subscriptionBilling') ?? ''} rows={billingRows(plan.subscriptionBillingPeriodCycle)} />
                    <CycleColumn icon="chart-column" title={t('packages.usageBilling') ?? ''} rows={billingRows(plan.usageBillingPeriodCycle)} />
                </div>
            </Card.Content>
        </Card>
    );
};

export default PackagePlanCard;
